import { toast } from 'react-toastify';

import axios from 'axios';
import { NATIVE_TOKENS, TOKEN_BY_ADDRESS, WOWMAX_API_URL, WRAPPED_NATIVE_TOKENS } from 'consts';
import { CT_API_URL, CT_CHAIN_ID, CT_CHAINS, GECKO_ID, MEME_API_URL } from 'consts/copyTrading';
import { makeAutoObservable, reaction, runInAction, when } from 'mobx';
import { CommonStore } from 'store/common/CommonStore';
import { Web3Store } from 'store/web3/Web3Store';
import {
  Contract,
  CTToken,
  PriceResponse,
  StarResponse,
  UserStarsResponse,
} from 'types/copyTrading';
import { Address, zeroAddress } from 'viem';

type GeckoTerminalPriceResponse = {
  data: {
    attributes: {
      token_prices: {
        [address: Address]: string;
      };
    };
  };
};

const MAX_FAVORITE = 100;

const initialFavoriteMap: Record<number, Address[]> = {};
CT_CHAINS.forEach((c) => (initialFavoriteMap[c] = []));

export class CopyTradingStore {
  contractsMap: Record<number, Contract> | null = null;
  favoriteMap = initialFavoriteMap;
  favoriteLoading = false;

  constructor(
    private web3: Web3Store,
    private commonStore: CommonStore
  ) {
    makeAutoObservable(this, {}, { autoBind: true });

    when(
      () => !!this.web3.account,
      () => {
        this.fetchContracts();
        this.fetchCtTokenPrice();
      }
    );

    reaction(
      () => this.web3.account,
      (address) => {
        if (address) {
          this.fetchUserStars(address);
        } else {
          CT_CHAINS.forEach((c) => (this.favoriteMap[c] = []));
        }
      },
      { fireImmediately: true }
    );
  }

  get contracts() {
    if (!this.contractsMap) return null;
    if (!this.contractsMap[CT_CHAIN_ID]) {
      console.error(`Copy Trading: Contract on ${CT_CHAIN_ID} not found`);
    }
    return this.contractsMap[CT_CHAIN_ID] || null;
  }

  get favorite() {
    return this.favoriteMap[CT_CHAIN_ID];
  }

  get ctTokenPrice() {
    return +this.commonStore.pricesMap[CT_CHAIN_ID]?.[zeroAddress] || null;
  }

  fetchContracts = async () => {
    if (this.contracts) return;
    try {
      const contractsMap: Record<number, Contract> = {};
      const response = await axios.get<Contract[]>(`${MEME_API_URL}/chains`);
      response.data.forEach((c) => (contractsMap[c.id] = c));

      runInAction(() => {
        this.contractsMap = contractsMap;
      });
    } catch (err) {
      console.error(err);
    }
  };

  fetchWrappedGeckoPrice = async () => {
    try {
      const tokenAddress = WRAPPED_NATIVE_TOKENS[CT_CHAIN_ID].address;
      const response = await axios.get<GeckoTerminalPriceResponse>(
        `https://api.geckoterminal.com/api/v2/simple/networks/${GECKO_ID[CT_CHAIN_ID]}/token_price/${tokenAddress}`
      );
      const price = response.data.data.attributes.token_prices[tokenAddress];
      this.commonStore.updatePrice(CT_CHAIN_ID, tokenAddress, price);
    } catch (err: any) {
      console.log('geckoterminal err ', err.message);
    }
  };

  fetchCtTokenPrice = async () => {
    try {
      const tokenAddress = WRAPPED_NATIVE_TOKENS[CT_CHAIN_ID].address;
      if (this.commonStore.pricesMap[CT_CHAIN_ID]?.[tokenAddress]) return;

      const response = await axios.post<{ Address: Address; Price: number }[]>(
        `${WOWMAX_API_URL}/chains/${CT_CHAIN_ID}/prices`,
        { baseTokens: [tokenAddress] }
      );
      const price = response.data[0].Price;
      this.commonStore.updatePrice(CT_CHAIN_ID, tokenAddress, String(price));
    } catch (err: any) {
      console.log('fetchCtTokenPrice err ', err.message);
      this.fetchWrappedGeckoPrice();
    }
  };

  fetchUserStars = async (userWallet: Address) => {
    try {
      this.favoriteLoading = true;
      const response = await axios.get<UserStarsResponse>(
        `${CT_API_URL}/api/user/${userWallet}/stars`
      );

      runInAction(() => {
        CT_CHAINS.forEach((c) => (this.favoriteMap[c] = []));
        this.favoriteMap[CT_CHAIN_ID] = response.data.stars.map(
          (s) => s.trader.toLowerCase() as Address
        );
        this.favoriteLoading = false;
      });
    } catch (err) {
      console.error('Failed to fetch user stars:', err);
      runInAction(() => {
        this.favoriteLoading = false;
      });
    }
  };

  addStar = async (traderAddress: Address): Promise<boolean> => {
    const userWallet = this.web3.account;
    if (!userWallet) {
      toast.info('Please connect your wallet');
      return false;
    }

    if (this.favoriteMap[CT_CHAIN_ID].length >= MAX_FAVORITE) {
      toast.info(`You can add a maximum of ${MAX_FAVORITE} wallets to your favorites`);
      return false;
    }

    const addr = traderAddress.toLowerCase() as Address;
    if (!this.favoriteMap[CT_CHAIN_ID].includes(addr)) {
      this.favoriteMap[CT_CHAIN_ID].push(addr);
    }

    try {
      await axios.post<StarResponse>(`${CT_API_URL}/api/traders/${traderAddress}/star`, null, {
        params: { user_wallet: userWallet },
      });
      return true;
    } catch (err) {
      runInAction(() => {
        this.favoriteMap[CT_CHAIN_ID] = this.favoriteMap[CT_CHAIN_ID].filter((t) => t !== addr);
      });
      console.error('Failed to add star:', err);
      toast.error('Failed to add to favorites');
      return false;
    }
  };

  removeStar = async (traderAddress: Address): Promise<boolean> => {
    const userWallet = this.web3.account;
    if (!userWallet) return false;

    const addr = traderAddress.toLowerCase() as Address;
    const previousFavorites = [...this.favoriteMap[CT_CHAIN_ID]];
    this.favoriteMap[CT_CHAIN_ID] = this.favoriteMap[CT_CHAIN_ID].filter((t) => t !== addr);

    try {
      await axios.delete<StarResponse>(`${CT_API_URL}/api/traders/${traderAddress}/star`, {
        params: { user_wallet: userWallet },
      });
      return true;
    } catch (err) {
      runInAction(() => {
        this.favoriteMap[CT_CHAIN_ID] = previousFavorites;
      });
      console.error('Failed to remove star:', err);
      toast.error('Failed to remove from favorites');
      return false;
    }
  };

  toggleFavorite = async (address: Address, isFavorite: boolean) => {
    if (isFavorite) {
      await this.removeStar(address);
    } else {
      await this.addStar(address);
    }
  };
}
